import { Box, Flex, Text } from "@chakra-ui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { List, ListItem } from "../../api/lists";
import { ReadingListDetailHeroCoverStack } from "./ReadingListDetailHeroCoverStack";
import { LockIcon } from "../icons/LockIcon";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";

// Mirrors managers/lists.php :: ListsTypesManager.
const LIST_TYPE_CUSTOM_RELEASES = 4; // chapters / individual releases

// The rail row reuses the same cover-stack component the detail pane
// renders (ReadingListDetailHeroCoverStack) so the visual treatment is
// identical between the list overview and the open list. Per Figma the
// individual cover is 142px tall on desktop; on mobile we drop to 92px
// so the cover doesn't eat 40% of a 360px-wide screen. coverHeight is a
// raw number on the underlying component (it scales internal dims off
// it), so we have to switch values from JS rather than a Chakra
// responsive prop.
const COVER_HEIGHT_DESKTOP = 142;
const COVER_HEIGHT_MOBILE = 92;

export interface ReadingListRailRowProps {
  list: List;
  onClick: () => void;
}

function formatStat(n?: number): string {
  if (n == null) return "0";
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

function sumViews(items: ListItem[]): number {
  let total = 0;
  for (const item of items) {
    const ctx = item.comic ?? item.release;
    const v = ctx?.total_pageviews;
    if (typeof v === "number") total += v;
  }
  return total;
}

// Compact month + year, e.g. "May 2026". Mirrors the Figma's stats line
// ("13 comics · 35k views · Created May 2026"). We deliberately don't
// include the day — the row is a glance-level surface and "month +
// year" reads clearly without crowding the line.
function formatCreatedAt(timestamp: string | null | undefined): string | null {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
}

export function ReadingListRailRow({
  list,
  onClick,
}: ReadingListRailRowProps) {
  const [t] = useTranslation();
  const isMobile = useMobileBreakpoint();
  const items = list.list_items ?? [];
  const itemCount = list.total_items ?? items.length;
  const isReleaseList = list.list_type_id === LIST_TYPE_CUSTOM_RELEASES;
  // Listing endpoints stamp `total_pageviews` (full-set aggregate, not just
  // the 3 thumbnailed items). Fall back to a per-item sum for older callers
  // / detail-endpoint payloads that still hand us full list_items.
  const totalViews = list.total_pageviews ?? sumViews(items);
  const createdAt = formatCreatedAt(list.create_time);
  const coverHeight = isMobile ? COVER_HEIGHT_MOBILE : COVER_HEIGHT_DESKTOP;

  // Vitals line — middle-dot separated, no icons (per Figma node
  // 3121:63386). Builds the segments first, then interleaves "·" so the
  // separator only appears between visible stats — no leading/trailing
  // dots and no double-dot when one stat is missing.
  const statSegments: string[] = [
    t(
      isReleaseList
        ? "screens.shelf.readingLists.releaseCount"
        : "screens.shelf.readingLists.comicCount",
      { count: itemCount },
    ),
  ];
  if (totalViews > 0) {
    statSegments.push(
      t("screens.shelf.readingLists.viewCount", {
        count: totalViews,
        // Override the `count` interpolation so we get formatted "35k"
        // instead of the raw integer. i18next's plural rules still pick
        // the right key (one/other) using the unformatted count above.
        formattedCount: formatStat(totalViews),
      }),
    );
  }
  if (createdAt) {
    statSegments.push(
      t("screens.shelf.readingLists.createdAt", { date: createdAt }),
    );
  }

  return (
    <Flex
      as="button"
      type="button"
      align="center"
      gap={{ base: "10px", md: "16px" }}
      w="100%"
      flexShrink={0}
      p={{ base: "10px", md: "14px" }}
      bg="rgba(255, 255, 255, 0.02)"
      borderRadius="8px"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.10)"
      textAlign="left"
      position="relative"
      transition="border-color 120ms ease-out, background 120ms ease-out"
      _hover={{
        bg: "rgba(255, 255, 255, 0.05)",
        borderColor: "rgba(255, 255, 255, 0.20)",
      }}
      onClick={onClick}
    >
      {/*
        Private indicator — sits in the top-right corner where the kebab
        used to live. Only renders for private lists; public ones are
        implicit. Non-clickable, label-only — there's no per-row action
        to expose here. `pointerEvents="none"` ensures hover never grabs
        focus from the underlying row-as-button.
      */}
      {!list.is_public && (
        <Box
          position="absolute"
          top={{ base: "10px", md: "14px" }}
          right={{ base: "10px", md: "14px" }}
          pointerEvents="none"
        >
          <PrivateBadge />
        </Box>
      )}
      {/*
        Same component the detail pane uses — identical diagonal X+Y
        stagger, identical custom-cover branch, just scaled down via
        the height prop. Keeps the rail-row covers visually consistent
        with the list-detail hero.
      */}
      <ReadingListDetailHeroCoverStack list={list} coverHeight={coverHeight} />
      <Flex
        direction="column"
        flex="1"
        minW={0}
        py="2px"
        // 24px between description and vitals on desktop per design
        // spec; tightened to 12 on mobile so the row stays compact.
        gap={{ base: "12px", md: "24px" }}
      >
        {/*
          Title↔description gap is 16 on desktop / 8 on mobile.
          Nested Flex so this gap doesn't cascade into the description
          ↔ vitals spacing handled by the parent column above.
        */}
        <Flex direction="column" gap={{ base: "8px", md: "16px" }} minW={0}>
          <Text
            fontFamily="Bricolage Grotesque"
            fontSize={{ base: "18px", md: "24px" }}
            fontWeight={600}
            lineHeight="1.2"
            color="dune.dune"
            noOfLines={1}
            wordBreak="break-word"
          >
            {list.display_name}
          </Text>
          {list.description && (
            <Text
              fontFamily="Roboto"
              fontSize={{ base: "13px", md: "16px" }}
              fontWeight={400}
              lineHeight="1.5"
              color="neutral.300"
              // 3 lines across all breakpoints — long curatorial
              // descriptions get the full envelope before truncating
              // to '…'.
              noOfLines={3}
              wordBreak="break-word"
            >
              {list.description}
            </Text>
          )}
        </Flex>
        <StatsLine segments={statSegments} />
      </Flex>
    </Flex>
  );
}

/**
 * Compact "Private" pill — eye-with-slash glyph + label. Same visual
 * weight as a stat segment so it doesn't compete with the title for
 * attention. Renders only for owner-visible private lists.
 */
function PrivateBadge() {
  const [t] = useTranslation();
  return (
    <Flex
      align="center"
      gap="4px"
      px="8px"
      py="3px"
      borderRadius="999px"
      bg="rgba(255, 255, 255, 0.06)"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.12)"
      color="neutral.300"
    >
      <LockIcon w="12px" h="12px" />
      <Text
        fontFamily="Roboto"
        fontWeight={600}
        fontSize="11px"
        lineHeight="1"
        letterSpacing="0.2px"
      >
        {t("screens.shelf.readingLists.layout.privateBadge")}
      </Text>
    </Flex>
  );
}

/**
 * Vitals line — `{a} · {b} · {c}` — interleaves the middle-dot only between
 * adjacent segments so we never render a leading/trailing dot or a
 * double-dot when one segment is filtered out upstream. Per Figma node
 * 3121:63386, this replaces the icon-prefixed stat chips that lived here
 * before.
 */
function StatsLine({ segments }: { segments: string[] }) {
  if (segments.length === 0) return null;
  return (
    <Flex
      direction="row"
      align="center"
      wrap="wrap"
      color="neutral.300"
      fontFamily="Roboto"
      fontWeight={600}
      fontSize={{ base: "13px", md: "16px" }}
      lineHeight="1.4"
      letterSpacing="0.1px"
      // Dot + segment are siblings (not nested in their own row Flex) so
      // adjacent items share a single column-gap — otherwise the inner gap
      // would double up with the outer gap and push segments apart
      // asymmetrically.
      sx={{ columnGap: "8px", rowGap: "2px" }}
    >
      {segments.map((segment, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <Text as="span" aria-hidden="true" color="neutral.500">
              ·
            </Text>
          )}
          <Text as="span">{segment}</Text>
        </Fragment>
      ))}
    </Flex>
  );
}
